import {
  DSKULength,
  maxASNLength,
  maxShipmentLength,
  maxZendeskLength,
  minASNLength,
  minShipmentLength,
  minZendeskLength,
  validASANAPattern,
  validASNPattern,
  validCurrencyPattern,
  validDSKU,
  validDateFormat,
  validLowerCaseIDPattern,
  validShipmentPattern,
  validWarehousePattern,
  validZendeskPattern,
} from "../../common/utils/config";
import {
  LostUnitsField,
  LostUnitsValidationErrorMessages,
} from "./LostUnitsForm.types";
import { RegisterOptions, ValidateResult } from "react-hook-form";

import { WarehousePublicClient } from "@deliverr/commons-clients";
import { mustBeDefined } from "../../common/utils/MustBeDefined";

const warehouseClient = new WarehousePublicClient({
  baseURL: mustBeDefined(process.env.REACT_APP_WAREHOUSE_SERVICE_URL),
});

const isValidDate = (input: string): ValidateResult => {
  if (input === "" || input === null) {
    return true;
  }

  const dTest: Date = new Date(input);

  if (isNaN(dTest.valueOf())) {
    return `Invalid date, date must be of form 2021-01-20`;
  }

  if (dTest > new Date() || dTest < new Date("2020-01-01")) {
    return "Date has to be between 2020-01-01 and today";
  }

  return true;
};
interface ValidationConfigMap {
  [name: string]: RegisterOptions;
}

const asnCheck: RegisterOptions = {
  minLength: {
    value: minASNLength,
    message: LostUnitsValidationErrorMessages.ASN_FORMAT,
  },
  maxLength: {
    value: maxASNLength,
    message: LostUnitsValidationErrorMessages.ASN_FORMAT,
  },
  pattern: {
    value: validASNPattern,
    message: LostUnitsValidationErrorMessages.ASN_FORMAT,
  },
};

const qtyCheck: RegisterOptions = {
  min: {
    value: 0,
    message: LostUnitsValidationErrorMessages.QTY_POSITIVE,
  },
};

export const validationConfig: ValidationConfigMap = {
  [LostUnitsField.DSKU]: {
    required: LostUnitsValidationErrorMessages.DSKU_REQUIRED,
    minLength: {
      value: DSKULength,
      message: LostUnitsValidationErrorMessages.DSKU_FORMAT,
    },
    maxLength: {
      value: DSKULength,
      message: LostUnitsValidationErrorMessages.DSKU_FORMAT,
    },
    pattern: {
      value: validDSKU,
      message: LostUnitsValidationErrorMessages.DSKU_FORMAT,
    },
  },
  [LostUnitsField.WAREHOUSE]: {
    validate: async (warehouseId: string) =>
      await warehouseClient
        .getWarehouse(warehouseId)
        .then(
          (result) =>
            result !== undefined ||
            LostUnitsValidationErrorMessages.WAREHOUSE_INVALID
        ),
  },
  [LostUnitsField.SHIPPINGPLAN]: {
    minLength: {
      value: minShipmentLength,
      message: LostUnitsValidationErrorMessages.SHIPMENT_FORMAT,
    },
    maxLength: {
      value: maxShipmentLength,
      message: LostUnitsValidationErrorMessages.SHIPMENT_FORMAT,
    },
    pattern: {
      value: validShipmentPattern,
      message: LostUnitsValidationErrorMessages.SHIPMENT_FORMAT,
    },
  },
  [LostUnitsField.FC_ASN]: asnCheck,
  [LostUnitsField.CROSSDOCK_ASN]: asnCheck,
  [LostUnitsField.LOST_QUANTITY]: {
    ...qtyCheck,
    required: LostUnitsValidationErrorMessages.QTY_REQUIRED,
  },

  [LostUnitsField.FOUND_QUANTITY]: qtyCheck,
  [LostUnitsField.REIMBURSED_AMOUNT]: {
    pattern: {
      value: validCurrencyPattern,
      message: LostUnitsValidationErrorMessages.REIMBURSED_FORMAT,
    },
  },
  [LostUnitsField.REASON]: {},
  [LostUnitsField.NOTES]: {},
  [LostUnitsField.SELLER]: {
    pattern: {
      value: validLowerCaseIDPattern,
      message: LostUnitsValidationErrorMessages.SELLER_FORMAT,
    },
  },
  [LostUnitsField.REPORTER]: {
    pattern: {
      value: validLowerCaseIDPattern,
      message: LostUnitsValidationErrorMessages.REPORTER_FORMAT,
    },
  },
  [LostUnitsField.ASANA]: {
    pattern: {
      value: validASANAPattern,
      message: LostUnitsValidationErrorMessages.ASANA_FORMAT,
    },
  },
  [LostUnitsField.ZENDESK]: {
    minLength: {
      value: minZendeskLength,
      message: LostUnitsValidationErrorMessages.ZENDESK_FORMAT,
    },
    maxLength: {
      value: maxZendeskLength,
      message: LostUnitsValidationErrorMessages.ZENDESK_FORMAT,
    },
    pattern: {
      value: validZendeskPattern,
      message: LostUnitsValidationErrorMessages.ZENDESK_FORMAT,
    },
  },
  [LostUnitsField.LOST_DATE]: {
    validate: {
      value: (value) => {
        return isValidDate(value);
      },
    },
    pattern: {
      value: validDateFormat,
      message: LostUnitsValidationErrorMessages.INVALID_DATE_FORMAT,
    },
  },
  [LostUnitsField.DISTRIBUTION]: {},
};
